import React from "react";
import { Grid, Typography, Box, CircularProgress } from "@mui/material";
import BookingTable from "components/Tables/BookingTable";
import FormDialog from "components/modal";
import BookingUpdateForm from "components/Forms/BookingUpdateForm";
import { usePropertyVisitBookings } from "services/hooks/rentals";
import TopSnack from "components/TopSnack";

const Booking = () => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [openUpdateDialog, setOpenUpdateDialog] = React.useState(false);
  const [selectedBooking, setSelectedBooking] = React.useState(null);
  const [status, setStatus] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [errors, setErrors] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [openSnack, setOpenSnack] = React.useState({
    open: false,
    vertical: "top",
    horizontal: "center",
  });
  const [severity, setSeverity] = React.useState("");
  const [message, setMessage] = React.useState("");
  
  // Fetch bookings from API
  const { bookings, loading, error, setNewData, updateBooking } = usePropertyVisitBookings({});

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleUpdateStatus = (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    if (booking) {
      setSelectedBooking(booking);
      setStatus(booking.status || "");
      setNotes(booking.notes || "");
      setErrors({});
      setOpenUpdateDialog(true);
    }
  };

  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false);
    setSelectedBooking(null);
    setStatus("");
    setNotes("");
    setErrors({});
  };

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
    if (errors.status) {
      setErrors((prev) => ({ ...prev, status: "" }));
    }
  };

  const handleNotesChange = (event) => {
    setNotes(event.target.value);
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack({ ...openSnack, open: false });
  };

  const handleSubmitUpdate = async () => {
    if (!status) {
      setErrors({ status: "Status is required" });
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const updateData = { status };
      if (notes) {
        updateData.notes = notes;
      }

      await updateBooking(selectedBooking.id, updateData);
      
      setOpenSnack({ ...openSnack, open: true });
      setSeverity("success");
      setMessage("Booking status updated successfully");
      handleCloseUpdateDialog();
    } catch (err) {
      setOpenSnack({ ...openSnack, open: true });
      setSeverity("error");
      setMessage(err.message || "Failed to update booking status");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m="20px">
        <Typography color="error">Error loading bookings: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Grid container spacing={2} style={{ marginTop: "20px" }}>
        <Grid item xs={12}>
          <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h4" component="h2" fontWeight="bold" fontSize="25px">
              Property Visit Bookings
            </Typography>
          </div>
          <BookingTable
            bookings={bookings || []}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            onUpdateStatus={handleUpdateStatus}
          />
        </Grid>
      </Grid>

      <FormDialog
        open={openUpdateDialog}
        handleClose={handleCloseUpdateDialog}
        enableActions={true}
        content={
          <BookingUpdateForm
            status={status}
            notes={notes}
            handleStatusChange={handleStatusChange}
            handleNotesChange={handleNotesChange}
            errors={errors}
          />
        }
        title="Update Booking Status"
        handleSubmit={handleSubmitUpdate}
        buttonText="Update"
        isSubmittingData={isSubmitting}
      />

      <TopSnack
        vertical={openSnack.vertical}
        horizontal={openSnack.horizontal}
        open={openSnack.open}
        handleCloseSnack={handleCloseSnack}
        message={message}
        severity={severity}
      />
    </Box>
  );
};

export default Booking;
